import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

/** Evento interno que sincroniza instâncias com a mesma chave na mesma aba (o `storage` nativo só dispara em outras abas). */
const SYNC_EVENT = "ds:local-storage-state";

interface SyncDetail {
  key: string;
  storage: Storage;
}

export interface UseLocalStorageStateOptions<T> {
  /** Converte o texto lido no valor; lance erro (ou valide) para rejeitar e cair no `initial`. Padrão: `JSON.parse`. */
  parse?: (raw: string) => T;
  /** Converte o valor em texto para gravar. Padrão: `JSON.stringify`. */
  serialize?: (value: T) => string;
  /** Storage usado no lugar do `localStorage` (ex.: `sessionStorage`). */
  storage?: Storage;
}

export type UseLocalStorageStateResult<T> = [
  value: T,
  setValue: Dispatch<SetStateAction<T>>,
  remove: () => void,
];

function resolveStorage(custom: Storage | undefined): Storage | null {
  if (custom) return custom;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readRaw(storage: Storage | null, key: string): string | null {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

/**
 * Estado persistido no `localStorage`, sincronizado entre instâncias (mesma aba) e abas.
 * Storage indisponível, erro de leitura/escrita ou valor inválido caem no `initial` sem quebrar.
 */
export function useLocalStorageState<T>(
  key: string,
  initial: T | (() => T),
  options: UseLocalStorageStateOptions<T> = {},
): UseLocalStorageStateResult<T> {
  const latest = useRef({ initial, options });
  latest.current = { initial, options };

  const getInitial = useCallback((): T => {
    const { initial: value } = latest.current;
    return value instanceof Function ? value() : value;
  }, []);

  const interpret = useCallback(
    (raw: string | null): { value: T; raw: string | null } => {
      if (raw === null) return { value: getInitial(), raw };
      try {
        const parse = latest.current.options.parse ?? ((text: string) => JSON.parse(text) as T);
        return { value: parse(raw), raw };
      } catch {
        return { value: getInitial(), raw };
      }
    },
    [getInitial],
  );

  const read = useCallback(
    () => interpret(readRaw(resolveStorage(latest.current.options.storage), key)),
    [key, interpret],
  );

  const [state, setState] = useState(read);
  const lastRaw = useRef(state.raw);
  const valueRef = useRef(state.value);
  // Chave nova: relê no mesmo render, sem um frame com o valor da chave anterior.
  const [stateKey, setStateKey] = useState(key);
  if (stateKey !== key) {
    const next = read();
    setStateKey(key);
    setState(next);
    valueRef.current = next.value;
    lastRaw.current = next.raw;
  } else {
    lastRaw.current = state.raw;
    valueRef.current = state.value;
  }

  useEffect(() => {
    const sync = () => {
      const raw = readRaw(resolveStorage(latest.current.options.storage), key);
      if (raw === lastRaw.current) return;
      const next = interpret(raw);
      lastRaw.current = raw;
      // Atualiza já o ref: um `setValue` funcional no mesmo handler deve partir do valor novo.
      valueRef.current = next.value;
      setState(next);
    };
    const onSync = (event: Event) => {
      const { key: changed, storage } = (event as CustomEvent<SyncDetail>).detail;
      if (changed === key && storage === resolveStorage(latest.current.options.storage)) sync();
    };
    const onStorage = (event: StorageEvent) => {
      const own = resolveStorage(latest.current.options.storage);
      if ((event.key === key || event.key === null) && event.storageArea === own) sync();
    };

    window.addEventListener(SYNC_EVENT, onSync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT, onSync);
      window.removeEventListener("storage", onStorage);
    };
  }, [key, interpret]);

  const notify = useCallback(() => {
    const storage = resolveStorage(latest.current.options.storage);
    if (storage) {
      window.dispatchEvent(new CustomEvent<SyncDetail>(SYNC_EVENT, { detail: { key, storage } }));
    }
  }, [key]);

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (action) => {
      const next = action instanceof Function ? action(valueRef.current) : action;
      const serialize = latest.current.options.serialize ?? JSON.stringify;
      let raw: string | null = null;
      let written = false;
      try {
        raw = serialize(next);
        resolveStorage(latest.current.options.storage)?.setItem(key, raw);
        written = true;
      } catch {
        // Cota cheia, janela privada ou valor não serializável: o estado em memória segue valendo.
      }
      valueRef.current = next;
      lastRaw.current = raw;
      setState({ value: next, raw });
      if (written) notify();
    },
    [key, notify],
  );

  const remove = useCallback(() => {
    let removed = false;
    try {
      resolveStorage(latest.current.options.storage)?.removeItem(key);
      removed = true;
    } catch {
      // Sem acesso ao storage: só volta ao inicial em memória.
    }
    const value = getInitial();
    valueRef.current = value;
    lastRaw.current = null;
    setState({ value, raw: null });
    if (removed) notify();
  }, [key, getInitial, notify]);

  return [state.value, setValue, remove];
}
